import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { websocketService } from '../services/websocket';

// Initial state
const initialState = {
  systemMetrics: {
    cpu: 0,
    memory: 0,
    disk: 0,
    network: { sent: 0, received: 0 },
    processes: 0,
  },
  sandboxStatus: {
    status: 'unknown',
    activeExecutions: 0,
    totalExecutions: 0,
    supportedLanguages: [],
    environments: [],
  },
  activeTests: [],
  notifications: [],
  websocketConnected: false,
  theme: 'dark',
  settings: {
    autoRefresh: true,
    refreshInterval: 5000,
    notifications: true,
    soundEnabled: false,
  },
};

// Action types
const ActionTypes = {
  UPDATE_SYSTEM_METRICS: 'UPDATE_SYSTEM_METRICS',
  UPDATE_SANDBOX_STATUS: 'UPDATE_SANDBOX_STATUS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  SET_WEBSOCKET_STATUS: 'SET_WEBSOCKET_STATUS',
  UPDATE_ACTIVE_TESTS: 'UPDATE_ACTIVE_TESTS',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',
  TOGGLE_THEME: 'TOGGLE_THEME',
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.UPDATE_SYSTEM_METRICS:
      return {
        ...state,
        systemMetrics: {
          ...state.systemMetrics,
          ...action.payload,
        },
      };

    case ActionTypes.UPDATE_SANDBOX_STATUS:
      return {
        ...state,
        sandboxStatus: {
          ...state.sandboxStatus,
          ...action.payload,
        },
      };

    case ActionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [
          ...state.notifications,
          {
            id: Date.now(),
            timestamp: new Date(),
            ...action.payload,
          },
        ],
      };

    case ActionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification.id !== action.payload
        ),
      };

    case ActionTypes.SET_WEBSOCKET_STATUS:
      return {
        ...state,
        websocketConnected: action.payload,
      };

    case ActionTypes.UPDATE_ACTIVE_TESTS:
      return {
        ...state,
        activeTests: action.payload,
      };

    case ActionTypes.UPDATE_SETTINGS:
      return {
        ...state,
        settings: {
          ...state.settings,
          ...action.payload,
        },
      };

    case ActionTypes.TOGGLE_THEME:
      return {
        ...state,
        theme: state.theme === 'dark' ? 'light' : 'dark',
      };

    default:
      return state;
  }
};

// Context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    // Initialize WebSocket connection
    websocketService.connect();
    
    // Set up WebSocket event listeners
    websocketService.onMessage('system_stats', (data) => {
      dispatch({
        type: ActionTypes.UPDATE_SYSTEM_METRICS,
        payload: {
          cpu: data.cpu_percent,
          memory: data.memory_percent,
          disk: data.disk_usage?.percent || 0,
          network: {
            sent: data.network_io?.bytes_sent || 0,
            received: data.network_io?.bytes_recv || 0,
          },
          processes: data.process_count || 0,
        },
      });
    });

    websocketService.onMessage('system_alert', (data) => {
      dispatch({
        type: ActionTypes.ADD_NOTIFICATION,
        payload: {
          type: 'warning',
          title: 'System Alert',
          message: data.message,
          severity: data.severity,
        },
      });
    });

    websocketService.onConnect(() => {
      dispatch({
        type: ActionTypes.SET_WEBSOCKET_STATUS,
        payload: true,
      });
      
      dispatch({
        type: ActionTypes.ADD_NOTIFICATION,
        payload: {
          type: 'success',
          title: 'Connected',
          message: 'Real-time monitoring connected',
          severity: 'success',
        },
      });
    });

    websocketService.onDisconnect(() => {
      dispatch({
        type: ActionTypes.SET_WEBSOCKET_STATUS,
        payload: false,
      });
      
      dispatch({
        type: ActionTypes.ADD_NOTIFICATION,
        payload: {
          type: 'warning',
          title: 'Disconnected',
          message: 'Real-time monitoring disconnected',
          severity: 'warning',
        },
      });
    });

    // Load settings from localStorage
    const savedSettings = localStorage.getItem('devsandbox-settings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        dispatch({
          type: ActionTypes.UPDATE_SETTINGS,
          payload: settings,
        });
      } catch (error) {
        // Silent error handling
      }
    }

    return () => {
      websocketService.disconnect();
    };
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('devsandbox-settings', JSON.stringify(state.settings));
  }, [state.settings]);

  // Actions
  const actions = {
    updateSystemMetrics: (metrics) => {
      dispatch({
        type: ActionTypes.UPDATE_SYSTEM_METRICS,
        payload: metrics,
      });
    },

    updateSandboxStatus: (status) => {
      dispatch({
        type: ActionTypes.UPDATE_SANDBOX_STATUS,
        payload: status,
      });
    },

    addNotification: (notification) => {
      dispatch({
        type: ActionTypes.ADD_NOTIFICATION,
        payload: notification,
      });
    },

    removeNotification: (id) => {
      dispatch({
        type: ActionTypes.REMOVE_NOTIFICATION,
        payload: id,
      });
    },

    updateActiveTests: (tests) => {
      dispatch({
        type: ActionTypes.UPDATE_ACTIVE_TESTS,
        payload: tests,
      });
    },

    updateSettings: (settings) => {
      dispatch({
        type: ActionTypes.UPDATE_SETTINGS,
        payload: settings,
      });
    },

    toggleTheme: () => {
      dispatch({
        type: ActionTypes.TOGGLE_THEME,
      });
    },
  };

  const value = {
    ...state,
    ...actions,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
